/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Guarantor Refund Button
 */
import { Button, ButtonProps, Form, Input, Modal, App } from 'antd';
import { useState } from 'react';
import { MODAL_TYPES } from '../modal';
import useGuarantorRefund from 'hooks/accounts/mutate/useGuarantorRefund';
import { getNameMeta } from 'utils/claims/institutionalClaimRequestParser';

export interface IProps extends ButtonProps {
	data: Guarantor.IGuarantorResponse;
}

const GuarantorRefund: React.FC<IProps> = ({ data, ...restProps }) => {
	const { modal } = App.useApp();
	const [open, setOpen] = useState(false);
	const [refundReason, setRefundReason] = useState('');
	const { mutateAsync, isPending } = useGuarantorRefund();

	const unallocatedAmount = data?.metadata?.unallocated;
	const guarantorName = getNameMeta(data?.fhirResource?.name)?.[0]?.fullName;
	const pendingPR = data?.metadata?.pendingPR;

	const handleCancel = () => {
		setOpen(false);
		setRefundReason('');
	};

	const handleSubmit = () => {
		mutateAsync(
			{
				guarantorId: data?.fhirResource?.id,
				guarantorRefundModel: {
					reason: refundReason,
					amount: unallocatedAmount,
				},
			},
			{ onSuccess: handleCancel },
		);
	};

	const handleClick = () => {
		if (pendingPR) {
			modal.confirm({
				...MODAL_TYPES.confirm,
				width: 520,
				content: `${guarantorName} has pending patient responsibility. Do you still want to record the unallocated amount as a refund?`,
				okText: 'Yes, Proceed',
				onOk: () => setOpen(true),
			});
		} else {
			setOpen(true);
		}
	};

	return (
		<>
			<Button type='primary' {...restProps} onClick={handleClick}>
				{'Record as Refund'}
			</Button>
			<Modal
				title='Refund Details'
				destroyOnHidden
				open={open}
				onCancel={handleCancel}
				width={600}
				okText='Record Refund'
				cancelText='Reset'
				onOk={handleSubmit}
				okButtonProps={{ loading: isPending, disabled: !refundReason }}
				cancelButtonProps={{ disabled: isPending }}
			>
				<Form layout='vertical' colon={false}>
					<Form.Item name='refundAmount' label='Amount ($)' rules={[{ required: true, message: 'Please enter refund amount' }]}>
						<Input type='number' defaultValue={unallocatedAmount} readOnly disabled />
					</Form.Item>
					<Form.Item name='refundReason' label='Reason' rules={[{ required: true, message: 'Please enter refund reason' }]}>
						<Input.TextArea
							placeholder='Enter refund reason'
							className='w-full'
							onChange={(e) => setRefundReason(e.target.value)}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default GuarantorRefund;
